import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { placeFixedPopover } from "../domains/fixed-popover";
import { onDocumentClickCapture } from "src/app/shared/ds/utils/listen-outside-angular";

export interface AssigneeItem {
  type: "employee" | "external";
  employee_id?: string;
  name: string;
  badge_color?: string;
  initials?: string;
}

/**
 * Task Assignee Picker - Selecteur d'assignes multi-selection custom
 * Design moderne style Todoist/Notion
 *
 * Usage:
 * <app-task-assignee-picker
 *   [(assignees)]="task.assignedTo"
 *   [employees]="employeesList"
 * ></app-task-assignee-picker>
 */
@Component({
  selector: "app-task-assignee-picker",
  templateUrl: "./task-assignee-picker.component.html",
  styleUrls: ["./task-assignee-picker.component.scss"],
})
export class TaskAssigneePickerComponent implements OnChanges, OnDestroy {
  /** Liste des assignes selectionnes */
  @Input() assignees: AssigneeItem[] = [];

  /** Liste des employes disponibles */
  @Input() employees: any[] = [];

  /** Placeholder quand vide */
  @Input() placeholder = "Assigner";

  /** Mode desactive */
  @Input() disabled = false;

  /** Nombre max d'avatars affiches avant "+N" */
  @Input() maxAvatars = 3;

  /** Emis quand la selection change */
  @Output() assigneesChange = new EventEmitter<AssigneeItem[]>();

  @ViewChild("searchInput") searchInput: ElementRef;

  /** Etat du dropdown */
  isOpen = false;

  /** Terme de recherche */
  searchTerm = "";

  /** Liste filtree des employes */
  filteredEmployees: AssigneeItem[] = [];

  /** Clic à l'extérieur, en capture : le galet arrête la propagation des clics */
  private readonly stopOutsideClick = onDocumentClickCapture(
    () => this.isOpen,
    (event) => this.onDocumentClick(event),
  );

  constructor(private elementRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["employees"] || changes["assignees"]) {
      this.updateFilteredEmployees();
    }
  }

  /** Toggle le dropdown */
  toggle(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchTerm = "";
      this.updateFilteredEmployees();
      this.revealDropdown();
    } else {
      this.close();
    }
  }

  /** Ouvre le dropdown */
  open(): void {
    if (this.disabled || this.isOpen) return;
    this.isOpen = true;
    this.searchTerm = "";
    this.updateFilteredEmployees();
    this.revealDropdown();
  }

  /** Ferme le dropdown */
  close(): void {
    this.isOpen = false;
    this.searchTerm = "";
    document.removeEventListener("scroll", this.onAncestorScroll, true);
  }

  /** Liste en position fixe : passe au-dessus des panneaux qui coupent leur contenu (tiroir des tâches) */
  private revealDropdown(): void {
    setTimeout(() => {
      this.positionDropdown();
      this.searchInput?.nativeElement?.focus();
      document.addEventListener("scroll", this.onAncestorScroll, true);
    });
  }

  private positionDropdown(): void {
    const host: HTMLElement = this.elementRef.nativeElement;
    const field = host.querySelector<HTMLElement>(".task-assignee-picker__field");
    const dropdown = host.querySelector<HTMLElement>(".task-assignee-picker__dropdown");
    if (!field || !dropdown) return;
    dropdown.style.width = `${Math.max(240, field.offsetWidth)}px`;
    placeFixedPopover(field, dropdown);
  }

  /** Suit le champ quand un panneau parent défile */
  private onAncestorScroll = (event: Event): void => {
    if (this.elementRef.nativeElement.contains(event.target)) return;
    this.positionDropdown();
  };

  @HostListener("window:resize")
  onResize(): void {
    if (this.isOpen) this.positionDropdown();
  }

  ngOnDestroy(): void {
    document.removeEventListener("scroll", this.onAncestorScroll, true);
  }

  /** Gestion du clic a l'exterieur */
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  /** Échap : fermer la liste seule (sans fermer le formulaire autour) et revenir sur le champ */
  @HostListener("keydown", ["$event"])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === "Escape" && this.isOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      this.focusField();
    }
  }

  /** Tab hors de la liste : la refermer */
  @HostListener("focusout", ["$event"])
  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (this.isOpen && next && !this.elementRef.nativeElement.contains(next)) this.close();
  }

  /** Entrée, Espace ou flèche bas sur le champ ouvrent la liste (pas sur la croix d'un assigné) */
  onFieldKeyDown(event: KeyboardEvent): void {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
      event.preventDefault();
      this.open();
    }
  }

  /**
   * Entrée dans la recherche : assigner le premier résultat, sans soumettre le formulaire autour,
   * puis refermer et revenir sur le champ pour que Tab passe au contrôle suivant (rouvrir pour en ajouter un autre)
   */
  selectFirstMatch(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.filteredEmployees.length) return;
    this.selectAssignee(this.filteredEmployees[0]);
    this.close();
    this.focusField();
  }

  private focusField(): void {
    setTimeout(() => this.elementRef.nativeElement.querySelector(".task-assignee-picker__field")?.focus());
  }

  /** Met a jour la liste filtree */
  updateFilteredEmployees(): void {
    const term = (this.searchTerm || "").toLowerCase().trim();
    const assignedIds = new Set(this.assignees.filter((a) => a.type === "employee").map((a) => a.employee_id));

    this.filteredEmployees = this.employees
      .filter((e) => {
        const fullName = `${e.first_name || ""} ${e.last_name || ""}`.toLowerCase();
        const matchesSearch = !term || fullName.includes(term);
        const notAlreadyAssigned = !assignedIds.has(e.employee_id);
        return matchesSearch && notAlreadyAssigned;
      })
      .slice(0, 10)
      .map((e) => ({
        type: "employee" as const,
        employee_id: e.employee_id,
        name: `${e.first_name || ""} ${e.last_name || ""}`.trim(),
        badge_color: e.badge_color || "#BEBEBE",
        initials: e.initials?.trim() || undefined,
      }));
  }

  /** Gestion de la saisie de recherche */
  onSearchInput(): void {
    this.updateFilteredEmployees();
  }

  /** Selectionne un assigne */
  selectAssignee(assignee: AssigneeItem): void {
    const newAssignees = [...this.assignees, assignee];
    this.assignees = newAssignees;
    this.assigneesChange.emit(newAssignees);
    this.searchTerm = "";
    this.updateFilteredEmployees();
    // Garder le dropdown ouvert pour multi-selection
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 50);
  }

  /** Retire un assigne */
  removeAssignee(assignee: AssigneeItem, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const newAssignees = this.assignees.filter(
      (a) => !(a.type === assignee.type && a.employee_id === assignee.employee_id && a.name === assignee.name),
    );
    this.assignees = newAssignees;
    this.assigneesChange.emit(newAssignees);
    this.updateFilteredEmployees();
  }

  /** Genere les initiales d'un nom */
  getInitials(name: string): string {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  /** Assignes affiches (limite par maxAvatars) */
  get displayedAssignees(): AssigneeItem[] {
    return this.assignees.slice(0, this.maxAvatars);
  }

  /** Nombre d'assignes caches */
  get hiddenCount(): number {
    return Math.max(0, this.assignees.length - this.maxAvatars);
  }

  /** Label pour le trigger */
  get triggerLabel(): string {
    if (this.assignees.length === 0) {
      return this.placeholder;
    }
    if (this.assignees.length === 1) {
      return this.assignees[0].name;
    }
    return `${this.assignees.length} personnes`;
  }
}
